import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useBrandTheme } from '../brand-theme';
import { isDefaultPackage, isCustomPackage, getPackageSupplementaryCost } from '../../../state/packages';
import { formatPrice } from '../../../state/offerings';
import type { NivaasPackage } from '../../../types/package';

interface EnhanceYourStayProps {
  /** All available packages */
  packages: NivaasPackage[];
  /** IDs of currently selected room offerings */
  selectedOfferingIds: string[];
  /** Currently selected package (null means no package selected) */
  selectedPackage: NivaasPackage | null;
  /** Callback when package selection changes */
  onSelectPackage: (pkg: NivaasPackage | null) => void;
  /** Messages for the component */
  messages?: {
    title?: string;
    subtitle?: string;
    baseLabel?: string;
    baseDescription?: string;
    noPackagesAvailable?: string;
    selectRoomsFirst?: string;
  };
}

const defaultMessages = {
  title: 'Enhance Your Stay',
  subtitle: 'Add meals, activities, and more to your booking',
  baseLabel: 'Base (Room Only)',
  baseDescription: 'Standard room pricing with no extras',
  noPackagesAvailable: 'No package upgrades available for your selection',
  selectRoomsFirst: 'Select rooms above to see available packages',
};

/**
 * Compact package selector integrated into the room selection step.
 * 
 * Features:
 * - Shows DEFAULT package as "Base" option
 * - Shows CUSTOM packages as upgrade options
 * - Strict filtering: only shows packages that cover ALL selected offerings
 * - Radio list format for scalability
 * - Shows total add-on cost per package
 */
export const EnhanceYourStay: React.FC<EnhanceYourStayProps> = ({
  packages,
  selectedOfferingIds,
  selectedPackage,
  onSelectPackage,
  messages: customMessages,
}) => {
  const { theme } = useBrandTheme();
  const messages = { ...defaultMessages, ...customMessages };

  // Find the DEFAULT (base) package
  const defaultPackage = useMemo(() => {
    return packages.find(isDefaultPackage) ?? null;
  }, [packages]);

  // Filter CUSTOM packages that cover ALL selected offerings
  const availableCustomPackages = useMemo(() => {
    if (selectedOfferingIds.length === 0) return [];

    return packages.filter((pkg) => {
      // Only include CUSTOM packages
      if (!isCustomPackage(pkg)) return false;

      // Check if package covers ALL selected offerings
      const packageOfferingIds = new Set(pkg.allowedOfferings.map((o) => o.id));
      return selectedOfferingIds.every((id) => packageOfferingIds.has(id));
    });
  }, [packages, selectedOfferingIds]);

  // Sort custom packages by supplementary cost (ascending)
  const sortedCustomPackages = useMemo(() => {
    return [...availableCustomPackages].sort((a, b) => {
      const costA = getPackageSupplementaryCost(a)?.amount ?? 0;
      const costB = getPackageSupplementaryCost(b)?.amount ?? 0;
      return costA - costB;
    });
  }, [availableCustomPackages]);

  // Determine if we have any rooms selected
  const hasRoomsSelected = selectedOfferingIds.length > 0;

  // Determine if base/default is selected
  const isBaseSelected = selectedPackage === null || (selectedPackage && isDefaultPackage(selectedPackage));

  return (
    <div className="mb-8 pb-6 border-b border-gray-200">
      <div className="mb-4">
        <h3 className="text-lg font-serif font-semibold text-gray-800">
          {messages.title}
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          {messages.subtitle}
        </p>
      </div>

      {!hasRoomsSelected ? (
        <div className="text-sm text-gray-400 italic py-4">
          {messages.selectRoomsFirst}
        </div>
      ) : (
        <div className="space-y-2">
          {/* Base (Default Package) Option */}
          <PackageOption
            label={messages.baseLabel}
            description={messages.baseDescription}
            isSelected={isBaseSelected}
            onSelect={() => onSelectPackage(defaultPackage)}
            theme={theme}
            isBase
          />

          {/* Custom Package Options */}
          {sortedCustomPackages.length > 0 ? (
            sortedCustomPackages.map((pkg) => {
              const supplementaryCost = getPackageSupplementaryCost(pkg);
              return (
                <PackageOption
                  key={pkg.id}
                  label={pkg.name}
                  description={pkg.description}
                  isSelected={selectedPackage?.id === pkg.id}
                  onSelect={() => onSelectPackage(pkg)}
                  addOnCost={supplementaryCost?.amount}
                  theme={theme}
                />
              );
            })
          ) : (
            <div className="text-sm text-gray-400 italic py-2">
              {messages.noPackagesAvailable}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface PackageOptionProps {
  label: string;
  description?: string;
  isSelected: boolean;
  onSelect: () => void;
  addOnCost?: number;
  isBase?: boolean;
  theme: ReturnType<typeof useBrandTheme>['theme'];
}

const PackageOption: React.FC<PackageOptionProps> = ({
  label,
  description,
  isSelected,
  onSelect,
  addOnCost,
  isBase,
  theme,
}) => {
  return (
    <div
      className={clsx(
        'flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all duration-150',
        {
          [`${theme.accentBorder} ${theme.accentBgLight}`]: isSelected,
          'border-gray-200 bg-white hover:border-gray-300': !isSelected,
        },
      )}
      onClick={onSelect}
      role="radio"
      aria-checked={isSelected}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      {/* Radio indicator */}
      <div
        className={clsx(
          'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5',
          {
            [`${theme.accentBorder} ${theme.accentBadge}`]: isSelected,
            'border-gray-300 bg-white': !isSelected,
          },
        )}
      >
        {isSelected && (
          <div className="w-2.5 h-2.5 rounded-full bg-white" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium text-gray-800">{label}</span>
          <span
            className={clsx(
              'text-sm font-medium px-2 py-0.5 rounded',
              isBase
                ? 'text-gray-500 bg-gray-100'
                : 'text-gray-700 bg-gray-100',
            )}
          >
            {isBase ? 'Included' : addOnCost ? `+${formatPrice({ amount: addOnCost, currency: 'INR' })}` : 'Included'}
          </span>
        </div>
        {description && (
          <p className="text-sm text-gray-500 mt-1 line-clamp-2">{description}</p>
        )}
      </div>
    </div>
  );
};

export default EnhanceYourStay;
